import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import swal from "sweetalert";
import {
  FaTags,
  FaPlus,
  FaSearch,
  FaPercentage,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaBan,
  FaRedo,
  FaTimes,
  FaBoxOpen,
  FaFolder,
  FaInfoCircle,
  FaExclamationTriangle
} from "react-icons/fa";

export default function DiscountView() {
  const [loading, setLoading] = useState(true);
  const [discountList, setDiscountList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const productRequest = useRef(0);

  // Form State
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    discount_title: "",
    discount_type: "P",
    amount: "",
    category_id: "",
    product_id: "",
    discount_start_date: moment().format("YYYY-MM-DD"),
    discount_end_date: moment().add(7, "days").format("YYYY-MM-DD"),
    status: "Y",
    isVisiable: "Y",
  });
  const [formErrors, setFormErrors] = useState({});

  // Filter & Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Action Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: null,
    title: "",
    actionType: "stop",
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    document.title = "Catalog Discounts & Promotions — Admin";
    fetchData();
  }, []);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      axios.get("/api/all-category").catch(() => ({ data: { status: 500, category: [] } })),
      axios.get("/api/fetch-discount").catch(() => ({ data: { status: 500, data: [] } })),
    ])
      .then(([catRes, discRes]) => {
        if (catRes.data.status === 200) {
          const categories = catRes.data.category || [];
          setCategoryList(categories.filter((cat) => String(cat.status) === "1"));
        }
        if (discRes.data.status === 200) {
          setDiscountList(discRes.data.data || []);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const loadCategoryProducts = (categoryId) => {
    const requestId = productRequest.current + 1;
    productRequest.current = requestId;
    if (!categoryId) {
      setProductList([]);
      setProductsLoading(false);
      return;
    }
    setProductsLoading(true);
    setProductList([]);
    axios
      .get(`/api/fetch-all-product?include_hidden=1&category_id=${categoryId}`)
      .then((res) => {
        if (requestId !== productRequest.current) return;
        setProductList(res.data.status === 200 ? res.data.data || [] : []);
        setProductsLoading(false);
      })
      .catch(() => {
        if (requestId !== productRequest.current) return;
        setProductList([]);
        setProductsLoading(false);
      });
  };

  // Safe Date Formatter
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith("0000") || !moment(dateStr).isValid()) return "—";
    return moment(dateStr).format("DD MMM YYYY");
  };

  // Compute Discount Status
  const getDiscountStatus = (item) => {
    if (item.status === "N" || item.isVisiable === "N" || item.deleted_at != null) {
      return {
        key: "stopped",
        label: "Stopped / Inactive",
        badgeClass: "bg-danger bg-opacity-10 text-danger border border-danger",
        icon: <FaBan size={10} className="me-1" />,
      };
    }

    const now = moment();
    const hasStart = item.discount_start_date && moment(item.discount_start_date).isValid();
    const hasEnd = item.discount_end_date && moment(item.discount_end_date).isValid();

    const startDate = hasStart ? moment(item.discount_start_date).startOf("day") : null;
    const endDate = hasEnd ? moment(item.discount_end_date).endOf("day") : null;

    if (endDate && now.isAfter(endDate)) {
      return {
        key: "expired",
        label: "Expired",
        badgeClass: "bg-secondary bg-opacity-10 text-secondary border border-secondary",
        icon: <FaClock size={10} className="me-1" />,
      };
    }

    if (startDate && now.isBefore(startDate)) {
      return {
        key: "scheduled",
        label: "Scheduled",
        badgeClass: "bg-info bg-opacity-10 text-primary border border-info",
        icon: <FaClock size={10} className="me-1" />,
      };
    }

    return {
      key: "active",
      label: "Active & Live",
      badgeClass: "bg-success bg-opacity-10 text-success border border-success",
      icon: <FaCheckCircle size={10} className="me-1" />,
    };
  };

  // KPI Analytics
  const stats = useMemo(() => {
    let active = 0;
    let scheduled = 0;
    let expired = 0;
    let stopped = 0;

    discountList.forEach((d) => {
      const st = getDiscountStatus(d);
      if (st.key === "active") active++;
      else if (st.key === "scheduled") scheduled++;
      else if (st.key === "expired") expired++;
      else if (st.key === "stopped") stopped++;
    });

    return {
      total: discountList.length,
      active,
      scheduled,
      expired,
      stopped,
    };
  }, [discountList]);

  // Filtered List
  const filteredDiscounts = useMemo(() => {
    return discountList.filter((item) => {
      const term = searchTerm.toLowerCase().trim();
      const titleMatch = (item.discount_title || "").toLowerCase().includes(term);
      const catMatch = (item.category?.name || "").toLowerCase().includes(term);
      const prodMatch = (item.product?.name || "").toLowerCase().includes(term);
      const matchesSearch = !term || titleMatch || catMatch || prodMatch;

      const itemStatus = getDiscountStatus(item);
      const matchesStatus = statusFilter === "all" || itemStatus.key === statusFilter;

      const matchesType = typeFilter === "all" || item.discount_type === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [discountList, searchTerm, statusFilter, typeFilter]);

  // Form Input Change Handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "category_id") {
      setFormData((prev) => ({ ...prev, category_id: value, product_id: "" }));
      setFormErrors((prev) => ({ ...prev, category_id: null, target: null }));
      loadCategoryProducts(value);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Create Discount Submit
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.discount_title.trim()) {
      errors.discount_title = "Campaign title is required.";
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      errors.amount = "A valid positive discount value is required.";
    }
    if (formData.discount_type === "P" && Number(formData.amount) > 100) {
      errors.amount = "Percentage discount cannot exceed 100%.";
    }
    if (!formData.category_id && !formData.product_id) {
      errors.target = "Select at least one Target Category or Product.";
    }
    if (!formData.discount_start_date) {
      errors.discount_start_date = "Start date is required.";
    }
    if (!formData.discount_end_date) {
      errors.discount_end_date = "End date is required.";
    }
    if (
      formData.discount_start_date &&
      formData.discount_end_date &&
      moment(formData.discount_start_date).isAfter(moment(formData.discount_end_date))
    ) {
      errors.discount_end_date = "End date must be on or after start date.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      swal("Missing or Invalid Fields", "Please fix highlighted form errors.", "warning");
      return;
    }

    setSaving(true);
    const authUser = sessionStorage.getItem("auth_name") || "System Admin";

    const payload = {
      discount_title: formData.discount_title.trim(),
      discount_type: formData.discount_type,
      amount: formData.amount,
      category_id: formData.category_id || null,
      product_id: formData.product_id || null,
      discount_start_date: formData.discount_start_date,
      discount_end_date: formData.discount_end_date,
      created_by: authUser,
      status: formData.status,
      isVisiable: formData.status === "Y" ? "Y" : "N",
    };

    axios
      .post("/api/discount", payload)
      .then((res) => {
        setSaving(false);
        if (res.data.status === 200) {
          swal("Campaign Created", res.data.message || "Discount added successfully.", "success");
          setShowCreateForm(false);
          setFormData({
            discount_title: "",
            discount_type: "P",
            amount: "",
            category_id: "",
            product_id: "",
            discount_start_date: moment().format("YYYY-MM-DD"),
            discount_end_date: moment().add(7, "days").format("YYYY-MM-DD"),
            status: "Y",
            isVisiable: "Y",
          });
          setFormErrors({});
          setProductList([]);
          fetchData();
        } else if (res.data.status === 422) {
          setFormErrors(res.data.errors || {});
          swal("Validation Error", "Please verify all required inputs.", "error");
        } else {
          swal("Access Denied", res.data.message || "Could not create discount.", "error");
        }
      })
      .catch((err) => {
        setSaving(false);
        const msg = err.response?.data?.message || "Server error while saving discount.";
        swal("Error", msg, "error");
      });
  };

  // Trigger Action Confirmation
  const openConfirmDialog = (id, title, actionType) => {
    setConfirmDialog({
      open: true,
      id,
      title,
      actionType,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, id: null, title: "", actionType: "stop" });
  };

  const executeDiscountAction = () => {
    if (!confirmDialog.id) return;
    setActionLoading(true);

    const isStopping = confirmDialog.actionType === "stop";
    const endpoint = isStopping
      ? "/api/edit-dicount-avaliable"
      : "/api/update-dicount-status-avaliable";

    const payload = {
      dicount_id: confirmDialog.id,
      discount_id: confirmDialog.id,
    };

    axios
      .post(endpoint, payload)
      .then((res) => {
        setActionLoading(false);
        closeConfirmDialog();
        if (res.data.status === 200) {
          swal(
            isStopping ? "Discount Stopped" : "Discount Activated",
            res.data.message || "Discount updated successfully.",
            "success"
          );
          fetchData();
        } else {
          swal("Error", res.data.message || "Could not update discount status.", "error");
        }
      })
      .catch(() => {
        setActionLoading(false);
        closeConfirmDialog();
        swal("Error", "Server error while updating discount status.", "error");
      });
  };

  return (
    <div className="container-fluid px-0">
      {/* Page Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Catalog Discounts & Sales
            </h3>
            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary fw-semibold px-2.5 py-1">
              Storewide Campaigns
            </span>
          </div>
          <p className="text-muted small mb-0 mt-1">
            Configure automatic percentage or fixed price markdowns applied directly to product categories or catalog items.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/coupons"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaPercentage /> Coupons & Promos
          </Link>
          <button
            type="button"
            onClick={() => setShowCreateForm(!showCreateForm)}
            className={`btn fw-semibold d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-sm ${
              showCreateForm ? "btn-secondary" : "btn-primary"
            }`}
          >
            {showCreateForm ? <FaTimes /> : <FaPlus />}
            {showCreateForm ? "Close Form" : "Create Discount Campaign"}
          </button>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="row g-3 mb-4">
        <div className="col-md-3 col-6">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Total Campaigns</span>
              <div className="admin-stat-icon" style={{ background: "#eff6ff", color: "#2563eb" }}>
                <FaTags />
              </div>
            </div>
            <div className="admin-stat-value text-dark">{stats.total}</div>
            <div className="admin-stat-footer text-muted">All historic and current offers</div>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Active & Live</span>
              <div className="admin-stat-icon" style={{ background: "#dcfce7", color: "#16a34a" }}>
                <FaCheckCircle />
              </div>
            </div>
            <div className="admin-stat-value text-success">{stats.active}</div>
            <div className="admin-stat-footer text-muted">Discounts actively applying to catalog</div>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Scheduled</span>
              <div className="admin-stat-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
                <FaClock />
              </div>
            </div>
            <div className="admin-stat-value text-warning">{stats.scheduled}</div>
            <div className="admin-stat-footer text-muted">Upcoming promotional sales</div>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Expired / Stopped</span>
              <div className="admin-stat-icon" style={{ background: "#fee2e2", color: "#dc2626" }}>
                <FaBan />
              </div>
            </div>
            <div className="admin-stat-value text-danger">{stats.expired + stats.stopped}</div>
            <div className="admin-stat-footer text-muted">
              {stats.expired} expired, {stats.stopped} manual stops
            </div>
          </div>
        </div>
      </div>

      {/* Priority Info Banner */}
      <div className="alert alert-light border rounded-3 p-3 mb-4 shadow-sm d-flex align-items-center gap-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{ width: "36px", height: "36px", background: "#eff6ff", color: "#2563eb" }}
        >
          <FaInfoCircle size={18} />
        </div>
        <div className="small text-muted flex-grow-1">
          <strong className="text-dark">Discount Precedence Hierarchy:</strong> If both Category and Product
          discounts are configured,{" "}
          <span className="text-primary fw-semibold">1) Category Discounts</span> take highest precedence, followed by{" "}
          <span className="text-primary fw-semibold">2) Individual Product Discounts</span>. Checkout coupons apply on top of final marked-down prices.
        </div>
      </div>

      {/* Collapsible Create Discount Form */}
      {showCreateForm && (
        <div className="admin-card mb-4 border-primary border-opacity-25 shadow-sm">
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: "32px", height: "32px", background: "#eff6ff", color: "#2563eb" }}
              >
                <FaPlus size={14} />
              </div>
              <h5 className="fw-bold mb-0 text-dark">Create New Catalog Discount</h5>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-light border rounded-circle"
              onClick={() => setShowCreateForm(false)}
            >
              <FaTimes size={12} />
            </button>
          </div>
          <div className="card-body p-4">
            <form onSubmit={handleCreateSubmit}>
              <div className="row g-3">
                {/* Title */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark small">
                    Campaign Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="discount_title"
                    value={formData.discount_title}
                    onChange={handleInputChange}
                    placeholder="e.g. Summer Flash Sale 2026"
                    className={`form-control rounded-3 ${formErrors.discount_title ? "is-invalid" : ""}`}
                  />
                  {formErrors.discount_title && (
                    <div className="invalid-feedback">{formErrors.discount_title}</div>
                  )}
                </div>

                {/* Discount Type */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold text-dark small">
                    Discount Type <span className="text-danger">*</span>
                  </label>
                  <select
                    name="discount_type"
                    value={formData.discount_type}
                    onChange={handleInputChange}
                    className="form-select rounded-3"
                  >
                    <option value="P">Percentage (%) Markdown</option>
                    <option value="F">Fixed Amount (PKR) Markdown</option>
                  </select>
                </div>

                {/* Discount Amount */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold text-dark small">
                    {formData.discount_type === "P" ? "Percentage Off (%)" : "Flat Price Off (PKR)"}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted">
                      {formData.discount_type === "P" ? "%" : "Rs."}
                    </span>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder={formData.discount_type === "P" ? "15" : "250"}
                      min="1"
                      max={formData.discount_type === "P" ? "100" : undefined}
                      className={`form-control ${formErrors.amount ? "is-invalid" : ""}`}
                    />
                    {formErrors.amount && <div className="invalid-feedback">{formErrors.amount}</div>}
                  </div>
                </div>

                {/* Target Category */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark small">
                    Target Category <small className="text-muted">(Optional)</small>
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    className={`form-select rounded-3 ${formErrors.target ? "is-invalid" : ""}`}
                  >
                    <option value="">— Select Target Category (Applies to all products in category) —</option>
                    {categoryList.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.target && <div className="invalid-feedback d-block">{formErrors.target}</div>}
                  <small className="text-muted" style={{ fontSize: "11.5px" }}>
                    Select a category to markdown all items under it automatically.
                  </small>
                </div>

                {/* Target Product */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-dark small">
                    Target Product <small className="text-muted">(Optional)</small>
                  </label>
                  <select
                    name="product_id"
                    value={formData.product_id}
                    onChange={handleInputChange}
                    className="form-select rounded-3"
                    disabled={!formData.category_id || productsLoading}
                  >
                    <option value="">
                      {!formData.category_id
                        ? "— Choose a category first —"
                        : productsLoading
                        ? "— Loading products —"
                        : productList.length === 0
                        ? "— No products in this category —"
                        : "— All products in this category —"}
                    </option>
                    {productList.map((prod) => (
                      <option key={prod.id} value={prod.id}>
                        {prod.name}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted" style={{ fontSize: "11.5px" }}>
                    {!formData.category_id
                      ? "Choose a category. Only that category’s products are loaded."
                      : productsLoading
                      ? "Loading products in this category."
                      : "Leave this blank to discount every product in the category, or pick one product."}
                  </small>
                </div>

                {/* Start Date */}
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-dark small">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    name="discount_start_date"
                    value={formData.discount_start_date}
                    onChange={handleInputChange}
                    className={`form-control rounded-3 ${formErrors.discount_start_date ? "is-invalid" : ""}`}
                  />
                  {formErrors.discount_start_date && (
                    <div className="invalid-feedback">{formErrors.discount_start_date}</div>
                  )}
                </div>

                {/* End Date */}
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-dark small">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    name="discount_end_date"
                    value={formData.discount_end_date}
                    onChange={handleInputChange}
                    className={`form-control rounded-3 ${formErrors.discount_end_date ? "is-invalid" : ""}`}
                  />
                  {formErrors.discount_end_date && (
                    <div className="invalid-feedback">{formErrors.discount_end_date}</div>
                  )}
                </div>

                {/* Publish Status */}
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-dark small">Publish Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-select rounded-3"
                  >
                    <option value="Y">Publish Immediately</option>
                    <option value="N">Draft / Pending</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="col-12 text-end pt-3 border-top mt-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn btn-light border px-4 py-2 me-2 rounded-3 fw-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary px-4 py-2 rounded-3 fw-semibold shadow-sm"
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving Campaign...
                      </>
                    ) : (
                      "Publish Discount Campaign"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Discounts Card */}
      <div className="admin-card">
        {/* Controls Toolbar */}
        <div className="admin-table-toolbar p-3 border-bottom bg-light bg-opacity-50">
          <div className="row g-2 align-items-center justify-content-between">
            {/* Search */}
            <div className="col-md-5 col-12">
              <div className="admin-search-wrapper position-relative">
                <FaSearch
                  className="position-absolute text-muted"
                  style={{ left: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "13px" }}
                />
                <input
                  type="text"
                  className="form-control form-control-sm rounded-pill ps-5 py-2"
                  placeholder="Search campaigns, categories, or products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ fontSize: "13px" }}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="col-md-7 col-12 d-flex flex-wrap align-items-center justify-content-md-end gap-2">
              <div className="d-flex align-items-center gap-1">
                <small className="text-muted fw-semibold">Status:</small>
                <select
                  className="form-select form-select-sm rounded-pill"
                  style={{ width: "auto", fontSize: "12.5px" }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses ({discountList.length})</option>
                  <option value="active">Active & Live ({stats.active})</option>
                  <option value="scheduled">Scheduled ({stats.scheduled})</option>
                  <option value="expired">Expired ({stats.expired})</option>
                  <option value="stopped">Stopped ({stats.stopped})</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-1">
                <small className="text-muted fw-semibold">Type:</small>
                <select
                  className="form-select form-select-sm rounded-pill"
                  style={{ width: "auto", fontSize: "12.5px" }}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="P">Percentage (%)</option>
                  <option value="F">Fixed (PKR)</option>
                </select>
              </div>

              {(searchTerm || statusFilter !== "all" || typeFilter !== "all") && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary rounded-pill px-2.5 py-1"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setTypeFilter("all");
                  }}
                  title="Reset Filters"
                >
                  <FaTimes size={11} className="me-1" /> Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 small fw-semibold">Loading Catalog Discounts...</p>
          </div>
        ) : filteredDiscounts.length === 0 ? (
          <div className="p-5 text-center">
            <div
              className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ width: "64px", height: "64px", background: "#f8fafc", color: "#94a3b8" }}
            >
              <FaTags size={28} />
            </div>
            <h5 className="fw-bold text-dark">No Discount Campaigns Found</h5>
            <p className="text-muted small mb-3">
              {searchTerm || statusFilter !== "all" || typeFilter !== "all"
                ? "Try adjusting your search query or filter settings."
                : "No catalog discounts configured yet. Click Create Discount Campaign to get started."}
            </p>
            {!showCreateForm && (
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="btn btn-sm btn-primary rounded-pill px-3 py-1.5 fw-semibold"
              >
                <FaPlus size={11} className="me-1" /> Create First Discount
              </button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: "13.5px" }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: "50px" }} className="text-center">#</th>
                  <th>Campaign Title</th>
                  <th>Discount Target</th>
                  <th>Value / Markdown</th>
                  <th>Duration (Start → End)</th>
                  <th>Status</th>
                  <th>Author</th>
                  <th className="text-end" style={{ width: "130px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDiscounts.map((item, idx) => {
                  const statusInfo = getDiscountStatus(item);
                  const isPct = item.discount_type === "P";
                  const isStopped = statusInfo.key === "stopped";
                  const isExpired = statusInfo.key === "expired";
                  const isActive = statusInfo.key === "active";

                  return (
                    <tr key={item.id}>
                      <td className="text-center text-muted fw-semibold" style={{ fontSize: "12px" }}>
                        {idx + 1}
                      </td>

                      {/* Title */}
                      <td>
                        <div className="fw-bold text-dark">{item.discount_title || "Untitled Campaign"}</div>
                        <code className="text-muted" style={{ fontSize: "11px" }}>
                          ID: #{item.id}
                        </code>
                      </td>

                      {/* Target Category or Product */}
                      <td>
                        {item.category && (
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1">
                              <FaFolder size={10} className="me-1" />
                              Category: {item.category.name}
                            </span>
                          </div>
                        )}
                        {item.product && (
                          <div className="d-flex align-items-center gap-1">
                            <span className="badge rounded-pill bg-dark bg-opacity-10 text-dark border px-2 py-1">
                              <FaBoxOpen size={10} className="me-1" />
                              Product: {item.product.name}
                            </span>
                          </div>
                        )}
                        {!item.category && !item.product && (
                          <span className="text-muted small">Global Storewide</span>
                        )}
                      </td>

                      {/* Value */}
                      <td>
                        <div className="fw-extrabold text-success font-monospace" style={{ fontSize: "14px" }}>
                          {isPct ? `-${item.amount}%` : `-Rs. ${Number(item.amount).toLocaleString()}`}
                        </div>
                        <small className="text-muted" style={{ fontSize: "11px" }}>
                          {isPct ? "Percentage Markdown" : "Fixed Cash Markdown"}
                        </small>
                      </td>

                      {/* Duration */}
                      <td>
                        <div className="d-flex flex-column" style={{ lineHeight: 1.3 }}>
                          <span className="fw-medium text-dark">
                            {formatDate(item.discount_start_date)}
                            <span className="text-muted mx-1">→</span>
                            {formatDate(item.discount_end_date)}
                          </span>
                          <small className="text-muted" style={{ fontSize: "11px" }}>
                            {isExpired
                              ? "Offer concluded"
                              : isActive
                              ? "Live promotional window"
                              : "Promotional timeline"}
                          </small>
                        </div>
                      </td>

                      {/* Status */}
                      <td>
                        <span
                          className={`badge rounded-pill px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center ${statusInfo.badgeClass}`}
                          style={{ fontSize: "11.5px" }}
                        >
                          {statusInfo.icon}
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Author & Audit */}
                      <td>
                        <div className="fw-semibold text-dark small">{item.created_by || "System Admin"}</div>
                        {item.delated_by && (
                          <small className="text-danger d-block" style={{ fontSize: "10.5px" }}>
                            Stopped by: {item.delated_by}
                          </small>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="text-end">
                        {isStopped ? (
                          <button
                            type="button"
                            onClick={() => openConfirmDialog(item.id, item.discount_title, "activate")}
                            className="btn btn-sm btn-outline-success rounded-pill px-2.5 py-1 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                            title="Reactivate this discount campaign"
                          >
                            <FaRedo size={10} /> Reactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openConfirmDialog(item.id, item.discount_title, "stop")}
                            className="btn btn-sm btn-outline-danger rounded-pill px-2.5 py-1 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                            title="Stop / Deactivate this discount"
                          >
                            <FaBan size={10} /> Stop
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Standalone Action Confirmation Modal */}
      {confirmDialog.open && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(15, 23, 42, 0.5)", backdropFilter: "blur(3px)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow-lg">
              <div className="modal-header border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className={`d-flex align-items-center justify-content-center rounded-circle ${
                      confirmDialog.actionType === "stop" ? "bg-danger text-white" : "bg-success text-white"
                    }`}
                    style={{ width: "32px", height: "32px" }}
                  >
                    {confirmDialog.actionType === "stop" ? <FaBan size={14} /> : <FaRedo size={14} />}
                  </div>
                  <h6 className="modal-title fw-bold text-dark">
                    {confirmDialog.actionType === "stop" ? "Stop Discount Campaign" : "Reactivate Discount Campaign"}
                  </h6>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeConfirmDialog}
                  disabled={actionLoading}
                ></button>
              </div>
              <div className="modal-body p-4">
                <p className="text-dark mb-2">
                  Are you sure you want to{" "}
                  <strong>{confirmDialog.actionType === "stop" ? "stop" : "reactivate"}</strong> the discount
                  campaign:
                </p>
                <div className="p-3 bg-light rounded-3 border fw-bold text-primary mb-3">
                  {confirmDialog.title || `Campaign #${confirmDialog.id}`}
                </div>
                <small className="text-muted d-block">
                  {confirmDialog.actionType === "stop"
                    ? "Stopping this campaign will immediately remove the markdown from products in store collections and cart calculation."
                    : "Reactivating this campaign will make the markdown live again for shoppers during the promotional window."}
                </small>
              </div>
              <div className="modal-footer border-top py-2.5">
                <button
                  type="button"
                  className="btn btn-sm btn-light border rounded-pill px-3 fw-semibold"
                  onClick={closeConfirmDialog}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                    confirmDialog.actionType === "stop" ? "btn-danger" : "btn-success"
                  }`}
                  onClick={executeDiscountAction}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Updating...
                    </>
                  ) : confirmDialog.actionType === "stop" ? (
                    "Confirm Stop"
                  ) : (
                    "Confirm Reactivation"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
